import { AlertDto, AlertSeverity } from '@act/shared/data-transfer-objects';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import { Fragment, ReactNode, useEffect } from 'react';
import useChangeLogDialog from 'react-components/dialog-forms/hooks/use-change-log-dialog';
import {
  alertAppSelectors,
  deleteAlert,
  getAlertsForMember,
  getGlobalAlerts,
} from 'redux/slices/alertSlice';
import { selectSelectedMemberId } from 'redux/slices/selectedMemberSlice';
import { useAppDispatch, useAppSelector } from 'redux/store-hooks';
import StyledAlert, { AlertAction } from './styled-alert';
import { StringHelper } from '@act/shared/utilities';
import { Button } from '@act/storybook';

/**
 * Props for `<AlertWrapper />`; expand this when necessary.
 *
 * @props mode: 'global' | 'member' - indicates whether to access global or member-specific alerts
 */
export interface AlertWrapperProps {
  mode: 'global' | 'member';
  actions?: AlertAction[];
}

/**
 * @props mode: 'global' | 'member' - indicates whether to access global or member-specific alerts
 *
 * @returns `AlertWrapper` - a wrapper that allows for interface with Redux; determines which alerts to display
 */
const AlertWrapper = ({ mode, actions }: AlertWrapperProps) => {
  const openChangeLogDialog = useChangeLogDialog();

  const dispatch = useAppDispatch();

  const selectedMemberId = useAppSelector(selectSelectedMemberId);
  const alertSliceStatus = alertAppSelectors.useSelectAlertSliceStatus();

  const alerts: AlertDto[] =
    mode === 'global'
      ? alertAppSelectors.useSelectGlobalAlerts()
      : alertAppSelectors.useSelectMemberAlertsByMember(selectedMemberId);

  useEffect(() => {
    async function loadAlerts() {
      if (mode === 'member' && selectedMemberId) {
        await dispatch(
          getAlertsForMember({ params: { memberId: selectedMemberId } })
        );
      }
      await dispatch(getGlobalAlerts({}));
    }

    if (alertSliceStatus !== 'loading' && !alerts) {
      void loadAlerts();
    }
  }, []);

  function assembleActions(
    alert: AlertDto,
    actions: AlertAction[] = []
  ): React.ReactNode {
    return (
      <Fragment>
        {actions.map(action => {
          if (action.icon) {
            return (
              <IconButton
                key={action.name}
                aria-label="Dismiss alert"
                onClick={() => action.callback()}
              >
                {action.icon}
              </IconButton>
            );
          }
          return (
            <Button
              key={action.name}
              variant="text"
              color="primary"
              onClick={() => action.callback()}
            >
              {StringHelper.sentenceCase(action.name)}
            </Button>
          );
        })}
        {alert.alertType === 'member_info_update' && (
          <Button
            onClick={() => openChangeLogDialog({ memberId: selectedMemberId })}
          >
            View updates
          </Button>
        )}
        {alert.isDismissable && (
          <IconButton
            aria-label="Dismiss alert"
            onClick={async () => {
              await dispatch(deleteAlert({ params: { alertId: alert?.id } }));
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Fragment>
    );
  }

  function buildProps(): {
    severity: AlertSeverity;
    mode: 'inline' | 'banner';
    message: string;
    title?: string;
    actions: ReactNode;
  } {
    if (!alerts) return;

    const firstAlert = alerts[0];

    const actionNode = assembleActions(firstAlert, actions);

    return {
      severity: firstAlert?.severity,
      mode: mode === 'global' ? 'inline' : 'banner',
      message: firstAlert?.message,
      ...(firstAlert?.title && { title: firstAlert.title }),
      actions: actionNode,
    };
  }

  return (
    <Fragment>
      {alerts?.length > 0 && <StyledAlert variant="filled" {...buildProps()} />}
    </Fragment>
  );
};

export default AlertWrapper;
